// Onboarding — name your customer AI assistant.
//
// Shown once to a not-yet-onboarded retailer (after the supplier picker, to
// avoid two dialogs at once): "name the assistant that looks after your
// customers 24/7". Skippable — until set, the customer portal defaults to
// "Aria". Saves via PUT /api/portal/settings/ai-assistant.
//
// Reuses the kos-onb modal styles from onboarding-suppliers.css.

import { iconMarkup } from "@tkos/shared-ui";
import type { PageContext } from "./types";
import "./onboarding-suppliers.css";

const SHOWN_KEY = "kos_onboarding_ai_assistant";

function alreadyHandled(): boolean {
  try { return !!localStorage.getItem(SHOWN_KEY); } catch { return false; }
}
function markHandled(payload: unknown): void {
  try { localStorage.setItem(SHOWN_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
}

/** Show the assistant-naming prompt if not onboarded and not already handled.
 *  Defers (no-op this load) while the supplier picker is open, so the two
 *  onboarding modals don't stack. No-op otherwise. */
export function maybeShowAiAssistantOnboarding(ctx: PageContext): void {
  if (ctx.user?.onboardingComplete === true) return;
  if (alreadyHandled()) return;
  if (document.getElementById("kos-onb-suppliers")) return; // let suppliers go first
  openAiAssistantOnboarding(ctx);
}

/** Force-open the prompt (e.g. from a "set up your assistant" link). */
export function openAiAssistantOnboarding(ctx: PageContext): void {
  if (document.getElementById("kos-onb-ai")) return;

  const dialog = document.createElement("dialog");
  dialog.id = "kos-onb-ai";
  dialog.className = "kos-onb";
  dialog.innerHTML = `
    <form method="dialog" class="kos-onb__form">
      <header class="kos-onb__head">
        <div class="kos-onb__head-ico">${iconMarkup("sparkle", { size: "md" })}</div>
        <div>
          <h2 class="kos-onb__title">Name your customer assistant</h2>
          <p class="kos-onb__sub">Every customer gets a friendly assistant on their project portal — it answers their questions about quotes, payments and delivery around the clock, and passes anything tricky to your team. Give it a name so it feels like one of your own.</p>
        </div>
      </header>
      <div class="kos-onb__body">
        <div class="kos-onb__group">
          <div class="kos-onb__group-title">Assistant name</div>
          <input type="text" class="kos-onb__add" data-role="name" placeholder="e.g. Emma, Alex, Sam" maxlength="100" />
        </div>
      </div>
      <footer class="kos-onb__foot">
        <button type="button" class="kos-onb__btn" data-role="skip">Skip for now</button>
        <button type="button" class="kos-onb__btn kos-onb__btn--primary" data-role="save">Save</button>
      </footer>
    </form>`;
  document.body.appendChild(dialog);

  const nameInput = dialog.querySelector<HTMLInputElement>("[data-role='name']")!;
  const saveBtn = dialog.querySelector<HTMLButtonElement>("[data-role='save']")!;
  const skipBtn = dialog.querySelector<HTMLButtonElement>("[data-role='skip']")!;
  const close = (): void => { if (dialog.open) dialog.close(); dialog.remove(); };

  skipBtn.addEventListener("click", () => { markHandled({ skipped: true }); close(); });

  const submit = (): void => {
    const name = nameInput.value.trim();
    if (!name) { nameInput.focus(); return; }
    saveBtn.disabled = true;
    void (async () => {
      try {
        await ctx.api("/portal/settings/ai-assistant", {
          method: "PUT",
          body: JSON.stringify({ name, enabled: true }),
        });
        markHandled({ name });
        ctx.showToast(`${name} is ready to help your customers.`, "success");
        close();
      } catch {
        ctx.showToast("Couldn't save — you can set this later in Settings → AI Assistant.", "error");
        saveBtn.disabled = false;
      }
    })();
  };
  saveBtn.addEventListener("click", submit);
  nameInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } });

  if (typeof dialog.showModal === "function") dialog.showModal();
  else dialog.setAttribute("open", "");
}
